import { useState } from "react";
import { RivalsCard } from "./panels";
import type { ClubView } from "../game/types";
import { Money, Panel } from "../ui/atoms";
import { useTelaEstreita } from "../ui/tela";
import { dinheiro, numero, rotuloDoMotor, useT, type Chave } from "../i18n";

/**
 * Administração do clube.
 *
 * As três alavancas que o motor já entendia mas a tela não oferecia: preço do
 * ingresso, obras de estrutura e empréstimo no banco. Como tudo mais, viram
 * decisão na fila e só valem quando a rodada roda.
 *
 * A curva de preço vem do mesmo cálculo que decide a bilheteria na partida, e
 * não de uma estimativa paralela, para a tela não prometer uma renda que o jogo
 * não vai pagar.
 */
export function ClubPanel({
  view,
  busy,
  onClubAction,
}: {
  view: ClubView;
  busy: boolean;
  onClubAction: (input: {
    ticketPrice?: number;
    loan?: number;
    repay?: number;
    build?: { facility: string; targetLevel: number };
  }) => void;
}) {
  const estreita = useTelaEstreita();
  const t = useT();
  const { management, club } = view;
  const [loan, setLoan] = useState(0);
  const [repay, setRepay] = useState(0);

  const best = management.ticketCurve.reduce((a, b) => (b.revenue > a.revenue ? b : a));
  const atual = management.ticketCurve.find((point) => point.current);

  return (
    <div className="grid" style={{ gap: 14 }}>
      {view.rivals.length > 0 && <RivalsCard view={view} />}

      <Panel title={t("clube.precoDoIngresso")}>
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 12 }}>
          {t("clube.explicacaoDoPreco")}
        </p>
        {atual && atual.attendance >= club.stadiumCapacity && best.price > atual.price && (
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>
            {t("clube.estadioLota", {
              preco: dinheiro(best.price),
              ganho: dinheiro(best.revenue - atual.revenue),
            })}
          </p>
        )}
        <div className="scroll">
          {/*
            A ocupação sai no celular, e é a única que pode sair.

            As outras quatro colunas são a decisão em si: por quanto cobrar,
            quanta gente vem, quanto entra e o botão de cobrar. A ocupação é as
            duas do meio divididas uma pela outra, então é a que se pode
            recalcular de cabeça, e era ela que empurrava a tabela 86 pixels
            para fora da tela.

            Nada de busca nem de ordenação aqui: a curva se lê na ordem do
            preço, que já é a ordem em que ela está.
          */}
          <table className="fixa">
            <thead>
              <tr>
                <th className="right">{t("clube.colunas.ingresso")}</th>
                <th className="right">{t("clube.colunas.publico")}</th>
                {!estreita && <th className="right">{t("clube.colunas.ocupacao")}</th>}
                <th className="right">{t("clube.colunas.renda")}</th>
                <th style={{ width: 76 }} />
              </tr>
            </thead>
            <tbody>
              {management.ticketCurve.map((point) => {
                const occupancy = Math.round((point.attendance / club.stadiumCapacity) * 100);
                return (
                  <tr
                    key={point.price}
                    className="player"
                    style={{ background: point.current ? "var(--primary-glow)" : "transparent" }}
                  >
                    <td className="right num" style={{ fontWeight: point.current ? 700 : 400 }}>
                      {dinheiro(point.price)}
                      {/* O selo desce em vez de ficar ao lado do preço: com a
                          largura de coluna valendo de verdade, "melhor renda"
                          ao lado do número não cabia num celular. */}
                      {point.current && (
                        <span className="label" style={{ display: "block" }}>
                          {t("clube.atual")}
                        </span>
                      )}
                      {point.price === best.price && !point.current && (
                        <span className="label-muted" style={{ display: "block" }}>
                          {t("clube.melhorRenda")}
                        </span>
                      )}
                    </td>
                    <td className="right num">{numero(point.attendance)}</td>
                    {!estreita && (
                      <td
                        className="right num"
                        style={{
                          color:
                            occupancy >= 95
                              ? "var(--success)"
                              : occupancy >= 60
                                ? "var(--warning)"
                                : "var(--danger)",
                        }}
                      >
                        {occupancy}%
                      </td>
                    )}
                    <td className="right num">{dinheiro(point.revenue)}</td>
                    <td className="right">
                      {!point.current && (
                        <button
                          className="ghost"
                          disabled={busy}
                          onClick={() => onClubAction({ ticketPrice: point.price })}
                          style={{ minHeight: 26, padding: "2px 10px", fontSize: 11 }}
                        >
                          {t("clube.cobrar")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={t("clube.estruturas")}>
        {management.build && (
          <div
            style={{
              padding: "10px 12px",
              marginBottom: 12,
              borderRadius: 8,
              background: "var(--surface-2)",
              border: "1px solid var(--primary)",
            }}
          >
            <span className="label">{t("clube.obraEmAndamento")}</span>
            <div style={{ marginTop: 4 }}>
              {t("clube.obraDescricao", {
                nome: rotuloDoMotor(management.build.nameKey),
                nivel: management.build.targetLevel,
                rodadas: management.build.roundsLeft,
              })}
            </div>
          </div>
        )}

        <div className="grid" style={{ gap: 10 }}>
          {management.facilities.map((facility) => {
            const affordable = facility.nextLevel !== null && facility.nextCost <= club.cash;
            return (
              <div
                key={facility.key}
                className="row"
                style={{ gap: 12, padding: "10px 12px", background: "var(--surface-2)", borderRadius: 8 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <strong>{rotuloDoMotor(facility.nameKey)}</strong>
                    <LevelDots level={facility.level} />
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    {t(facility.effectKey as Chave)}
                  </div>
                  <div className="label-muted" style={{ marginTop: 4 }}>
                    {t("clube.manutencao", { valor: dinheiro(facility.upkeep) })}
                  </div>
                </div>

                {facility.nextLevel === null ? (
                  <span className="label">{t("clube.noMaximo")}</span>
                ) : (
                  <div style={{ textAlign: "right" }}>
                    <div className="num" style={{ fontWeight: 700 }}>
                      {dinheiro(facility.nextCost)}
                    </div>
                    <div className="label-muted">
                      {t("clube.rodadasDeObra", { rodadas: facility.nextRounds })}
                    </div>
                    <button
                      className="ghost"
                      disabled={busy || !affordable || management.build !== null}
                      onClick={() =>
                        onClubAction({
                          build: { facility: facility.key, targetLevel: facility.nextLevel as number },
                        })
                      }
                      style={{ marginTop: 6, minHeight: 28, padding: "3px 10px", fontSize: 11 }}
                    >
                      {t("clube.subirPara", { nivel: facility.nextLevel })}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {management.payments.length > 0 && (
        <Panel
          title={t("clube.parcelas")}
          action={
            <span className="label-muted">
              {t("clube.parcelasResumo", {
                aPagar: dinheiro(management.owed),
                aReceber: dinheiro(management.receivable),
              })}
            </span>
          }
          padded={false}
        >
          <table>
            <thead>
              <tr>
                <th>{t("clube.colunasParcelas.jogador")}</th>
                <th>{t("clube.colunasParcelas.fluxo")}</th>
                <th className="right">{t("clube.colunasParcelas.venceEm")}</th>
                <th className="right">{t("clube.colunasParcelas.valor")}</th>
              </tr>
            </thead>
            <tbody>
              {management.payments.map((payment, index) => (
                <tr key={`${payment.playerName}-${index}`} className="player">
                  <td style={{ fontWeight: 600 }}>{payment.playerName}</td>
                  <td className="muted" style={{ fontSize: 11.5 }}>
                    {payment.incoming ? t("clube.aReceber") : t("clube.aPagar")}
                  </td>
                  <td className="right num">
                    {payment.dueIn === 0
                      ? t("clube.nestaRodada")
                      : t("clube.emRodadas", { rodadas: payment.dueIn })}
                  </td>
                  <td
                    className="right num"
                    style={{ color: payment.incoming ? "var(--success)" : "var(--danger)" }}
                  >
                    {payment.incoming ? "+" : "-"}
                    {dinheiro(payment.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title={t("clube.banco")}>
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginBottom: 14 }}
        >
          <Stat label={t("clube.caixa")} value={dinheiro(club.cash)} />
          <Stat
            label={t("clube.divida")}
            value={dinheiro(management.debt)}
            danger={management.debt > 0}
          />
          <Stat label={t("clube.credito")} value={dinheiro(management.loanAvailable)} />
          <Stat
            label={t("clube.juros")}
            value={t("clube.jurosValor", {
              taxa: (management.debtRatePerRound * 100).toFixed(2),
            })}
          />
        </div>

        <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
          {t("clube.explicacaoDoBanco")}
          {management.owed > 0 &&
            ` ${t("clube.descontoDeParcelas", { valor: dinheiro(management.owed) })}`}
        </p>

        <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
          <div>
            <div className="label-muted" style={{ marginBottom: 4 }}>
              {t("clube.pegarEmprestado")}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <input
                type="number"
                min={0}
                step={1_000_000}
                max={management.loanAvailable}
                value={loan}
                onChange={(e) => setLoan(Math.max(0, Number(e.target.value)))}
                style={{ width: 150 }}
              />
              <button
                disabled={busy || loan <= 0 || loan > management.loanAvailable}
                onClick={() => onClubAction({ loan })}
              >
                {t("clube.pedir")}
              </button>
            </div>
          </div>

          {management.debt > 0 && (
            <div>
              <div className="label-muted" style={{ marginBottom: 4 }}>
                {t("clube.abaterDivida")}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <input
                  type="number"
                  min={0}
                  step={1_000_000}
                  max={Math.min(management.debt, club.cash)}
                  value={repay}
                  onChange={(e) => setRepay(Math.max(0, Number(e.target.value)))}
                  style={{ width: 150 }}
                />
                <button
                  disabled={busy || repay <= 0 || repay > club.cash}
                  onClick={() => onClubAction({ repay })}
                >
                  {t("clube.pagar")}
                </button>
              </div>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function LevelDots({ level }: { level: number }) {
  const t = useT();
  return (
    <span className="row" style={{ gap: 3 }} aria-label={t("clube.nivelAria", { nivel: level })}>
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: i < level ? "var(--primary)" : "var(--surface-3)",
          }}
        />
      ))}
    </span>
  );
}

function Stat({ label, value, danger = false }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <div className="label-muted">{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          marginTop: 2,
          color: danger ? "var(--danger)" : "inherit",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export { Money };
